import { useEffect, useState } from "react";
import {
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  Flag,
  History,
  Loader2,
  Route as RouteIcon,
  TrendingUp,
} from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StageDates } from "@/components/StageDates";
import { api, apiError, fmtDay } from "@/lib/api";

const KIND = {
  stage: { label: "Stage", icon: Flag, colour: "hsl(var(--primary))" },
  milestone: { label: "Milestone", icon: CheckCircle2, colour: "hsl(152 55% 38%)" },
  revision: { label: "Date moved", icon: CalendarClock, colour: "hsl(36 92% 44%)" },
  reading: { label: "Reading", icon: ClipboardList, colour: "hsl(199 89% 48%)" },
  trail: { label: "Portal", icon: History, colour: "hsl(0 0% 55%)" },
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthOf = (day) => `${MONTHS[Number(day.slice(5, 7)) - 1]} ${day.slice(0, 4)}`;

const Tip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="border border-foreground bg-card p-2 text-xs">
      <div className="font-data">{fmtDay(row.day)}</div>
      <div className="font-display font-bold">{row.stage}</div>
    </div>
  );
};

/** How one use case has travelled — the stage rail, the line over time, and every dated step. */
export const JourneyView = ({ projectId, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [kinds, setKinds] = useState([]);

  useEffect(() => {
    if (!open || !projectId) return;
    setData(null);
    setError("");
    api
      .get(`/projects/${projectId}/journey`)
      .then((r) => setData(r.data))
      .catch((err) => setError(apiError(err)));
  }, [open, projectId]);

  const p = data?.project;
  const events = (data?.events || []).filter((e) => !kinds.length || kinds.includes(e.kind));
  const line = (data?.line || []).map((r) => ({ ...r, label: fmtDay(r.day) }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[92vh] max-w-5xl overflow-y-auto rounded-none border-border bg-background"
        data-testid="journey-view"
      >
        <DialogHeader className="text-left">
          <div className="label-caps text-primary">Use case journey</div>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">
            {p ? `${p.code} — ${p.title}` : "Use case journey"}
          </DialogTitle>
        </DialogHeader>

        {error && (
          <p className="text-sm text-muted-foreground" data-testid="journey-error">
            {error}
          </p>
        )}
        {!data && !error && (
          <div className="flex h-48 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {data && (
          <div className="space-y-6">
            <div className="border border-border bg-card p-4">
              <div className="flex flex-wrap items-center gap-2">
                <RouteIcon className="h-3.5 w-3.5 text-primary" />
                <span className="label-caps">
                  {p.route === "procurement" ? "Vendor-led road" : "Delivery road"} · standing at{" "}
                  {p.phase || "—"}
                </span>
              </div>
              <div className="mt-3">
                <StageDates project={{ ...p, code: p.code }} />
              </div>
              <div className="font-data mt-3 flex flex-wrap gap-3 text-[11px] text-muted-foreground">
                <span>{p.plant}</span>
                <span>{p.status}</span>
                {p.owner_name && <span>looked after by {p.owner_name}</span>}
                <span>{data.events.length} step(s) on record</span>
              </div>
            </div>

            {line.length > 1 && (
              <div className="border border-border bg-card" data-testid="journey-line">
                <div className="border-b border-border p-4">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-3.5 w-3.5 text-primary" />
                    <span className="label-caps">Progress over time</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Which sub-stage it stood on, each day something was recorded. A flat stretch is a
                    stall.
                  </p>
                </div>
                <div className="p-4">
                  <div className="h-[220px] w-full min-w-[280px]">
                    <ResponsiveContainer width="100%" height={220} minWidth={280} debounce={40}>
                      <LineChart data={line} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                        <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }}
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis
                          domain={[0, Math.max(1, (p.stages?.length || 2) - 1)]}
                          ticks={(p.stages || []).map((_s, i) => i)}
                          tickFormatter={(i) => (p.stages?.[i] || "").slice(0, 14)}
                          width={110}
                          tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }}
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <Tooltip content={<Tip />} />
                        <Line
                          type="stepAfter"
                          dataKey="stage_index"
                          stroke="hsl(var(--primary))"
                          strokeWidth={2}
                          dot={{ r: 3 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            <div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="label-caps">Step by step</span>
                <div className="flex flex-wrap gap-1.5" data-testid="journey-filters">
                  {Object.entries(KIND).map(([key, k]) => (
                    <button
                      key={key}
                      type="button"
                      data-testid={`journey-filter-${key}`}
                      onClick={() =>
                        setKinds((v) => (v.includes(key) ? v.filter((x) => x !== key) : [...v, key]))
                      }
                      className={`font-display border px-2 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors duration-200 ${
                        kinds.includes(key) || !kinds.length
                          ? "text-foreground"
                          : "text-muted-foreground"
                      }`}
                      style={{
                        borderColor:
                          kinds.includes(key) || !kinds.length ? k.colour : "hsl(var(--border))",
                      }}
                    >
                      {k.label} · {data.counts[key] || 0}
                    </button>
                  ))}
                </div>
              </div>

              <ol className="mt-3 border border-border bg-card" data-testid="journey-events">
                {events.map((e, i) => {
                  const k = KIND[e.kind] || KIND.trail;
                  const Icon = k.icon;
                  const newMonth = i === 0 || monthOf(events[i - 1].day) !== monthOf(e.day);
                  return (
                    <li key={`${e.day}-${e.title}-${i}`}>
                      {newMonth && (
                        <div className="font-data border-y border-border bg-muted px-4 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                          {monthOf(e.day)}
                        </div>
                      )}
                      <div
                        className="flex gap-3 border-b border-border px-4 py-3 last:border-b-0"
                        data-testid={`journey-event-${i}`}
                      >
                        <div className="pt-0.5">
                          <Icon className="h-4 w-4" style={{ color: k.colour }} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-baseline justify-between gap-2">
                            <span className="text-sm font-semibold">{e.title}</span>
                            <span className="font-data text-[11px] text-muted-foreground">
                              {fmtDay(e.day)}
                            </span>
                          </div>
                          {e.detail && (
                            <p className="mt-1 border-l-2 border-border pl-3 text-[11px] text-muted-foreground">
                              {e.detail}
                            </p>
                          )}
                          <div className="font-data mt-1 flex flex-wrap gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                            <span style={{ color: k.colour }}>{k.label}</span>
                            {e.stage && <span>{e.stage}</span>}
                            {e.revision && <span>{e.revision}</span>}
                            {e.by && <span>by {e.by}</span>}
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
                {!events.length && (
                  <li className="p-8 text-center text-sm text-muted-foreground">
                    Nothing has been recorded against this use case yet.
                  </li>
                )}
              </ol>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
